import i18n from '../i18n';

/**
 * Espera a que estén cargadas las traducciones de las etiquetas de catálogo.
 *
 * ── El problema ──────────────────────────────────────────────────────────
 * Las traducciones se cargan por HTTP (`i18next-http-backend`,
 * `/locales/{{lng}}/{{ns}}.json`), así que hay una ventana en la que
 * `i18n.t()` todavía no tiene el bundle y devuelve la CLAVE.
 *
 * Eso sería inocuo si el texto se pintara en un componente —React vuelve a
 * renderizar cuando el bundle llega—, pero las etiquetas de las tablas se
 * calculan en los SELECTORES, al resolver la fila, y el resultado se queda
 * congelado dentro del dato. Si el selector gana la carrera, la celda enseña
 * la clave en crudo **para siempre**: ni recargando se arregla, porque en la
 * recarga vuelve a ocurrir lo mismo.
 *
 * Medido en beta el 2026-09-02 (antes de este helper), con la aplicación en
 * castellano y en un listado cargado en frío:
 *
 *   Sentido → "surveillance.directionBoth (↕)"   en vez de "Ambos sentidos (↕)"
 *   Sentido → "workOrder.unspecified"            en vez de "Sin especificar"
 *   Carril  → "Esquerre"                         en vez de "Izquierdo"
 *
 * El carril salía en catalán y no en crudo porque `catalogLabels` sí pasa un
 * `defaultValue` (el nombre del catálogo, escrito en catalán); es un respaldo
 * mejor que la clave, pero sigue siendo el idioma equivocado.
 *
 * ── Por qué aquí y no en cada helper ─────────────────────────────────────
 * `laneLabel`/`trackLabel`/`formatDirectionCellLabel` son SÍNCRONOS y puros
 * —eso es lo que permite usarlos igual en la tabla, en el Excel y en el PDF—,
 * así que no pueden esperar nada. Quien puede esperar es el selector, que ya
 * es asíncrono. De ahí que la espera se haga una vez, arriba, y los helpers
 * se queden como están.
 *
 * ── Coste ────────────────────────────────────────────────────────────────
 * Ninguno en la práctica: `loadNamespaces` resuelve de inmediato si los
 * bundles ya están, que es el caso a partir del primer render. Y si la red
 * falla (tablet sin cobertura) se sigue adelante: los helpers ya tienen su
 * respaldo, que es justo el comportamiento anterior.
 */

/** Namespaces de los que salen las etiquetas de catálogo de las tablas. */
const LABEL_NAMESPACES = [
  // laneLabel / trackLabel / hoursTypeLabel → forms:lanes.*, forms:tracks.*
  'forms',
  // formatDirectionCellLabel → common:workOrder.unspecified
  'common',
  // formatDirectionCellLabel → pages:surveillance.direction*
  'pages',
];

export const ensureLabelNamespaces = async (): Promise<void> => {
  try {
    await i18n.loadNamespaces(LABEL_NAMESPACES);
  } catch {
    // Sin cobertura seguimos: cada helper cae a su propio respaldo.
  }
};
